import { useContext } from 'react';
import useSWRInfinite from 'swr/infinite';

import {
    ChainlitAPI,
    ChainlitContext,
    IPageInfo,
    IThread
} from '@chainlit/react-client';

export const DEEP_INTELLIGENCE_THREADS_KEY = 'deep-intelligence-threads';

const PAGE_SIZE = 20;

interface ThreadsPage {
    data: IThread[];
    pageInfo: IPageInfo;
}

type ThreadsPageKey = readonly [string, number, string | null];

export function getDeepIntelligenceThreadsKey(
    pageIndex: number,
    previousPage: ThreadsPage | null
): ThreadsPageKey | null {
    if (previousPage && !previousPage.pageInfo.hasNextPage) return null;
    const cursor = previousPage?.pageInfo.endCursor ?? null;
    return [DEEP_INTELLIGENCE_THREADS_KEY, pageIndex, cursor];
}

async function fetchPage(
    apiClient: ChainlitAPI,
    cursor: string | null
): Promise<ThreadsPage> {
    const { data, pageInfo } = await apiClient.listThreads(
        { first: PAGE_SIZE, cursor: cursor ?? undefined },
        { isDeepIntelligence: true }
    );
    return { data: data || [], pageInfo };
}

export function useDeepIntelligenceThreads() {
    const apiClient = useContext(ChainlitContext);

    const {
        data: pages,
        error,
        isLoading,
        size,
        setSize,
        mutate
    } = useSWRInfinite<ThreadsPage>(
        getDeepIntelligenceThreadsKey,
        ([, , cursor]: ThreadsPageKey) => fetchPage(apiClient, cursor),
        {
            revalidateOnFocus: false,
            revalidateFirstPage: false,
            dedupingInterval: 5000
        }
    );

    const threads = (pages ?? []).flatMap((page) => page.data);
    const lastPage = pages?.[pages.length - 1];

    const hasMore = lastPage ? lastPage.pageInfo.hasNextPage : true;

    const isLoadingMore =
        isLoading ||
        (size > 0 && !!pages && typeof pages[size - 1] === 'undefined');

    const loadMore = () => {
        if (!hasMore || isLoadingMore) return;
        setSize(size + 1);
    };

    const refresh = () => mutate();

    return {
        threads,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        error,
        refresh
    };
}
